import Item from 'src/models/item/Item'
import ItemVariation from 'src/models/item/ItemVariation'
import Model, { encoders, ID } from 'src/models/model'
import ItemOrderItemModifier from 'src/models/order-item/ItemOrderItemModifier'
import User from 'src/models/user/User'

export default class ItemOrderItem extends Model {
  static name = 'ItemOrderItem'

  static versioned = true

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      item: {
        type: Item,
        output: true,
        reference: true
      },
      itemVariation: {
        type: ItemVariation,
        encode: encoders.hasOne,
        reference: true
      },
      itemModifiers: {
        type: [ItemOrderItemModifier],
        required: true
      },
      itemModifierNames: {
        type: [String],
        output: true,
        excludeFromDefault: true
      },
      user: {
        type: User,
        encode: encoders.hasOne,
        reference: true
      },
      price: {
        type: Number,
        output: true,
        computed () {
          let price = 0
          if (this.itemVariation) {
            price += this.itemVariation.price || 0
          }
          _.each(this.itemModifiers?.data, (itemModifier) => {
            price += itemModifier?.modifier?.price || 0
          })
          return price
        }
      },
      adminApproved: {
        type: Boolean,
        default: false,
        local: true
      }
    }
  }

  itemVariationSelected (itemVariation) {
    return this.itemVariation && this.itemVariation.id === itemVariation.id
  }

  toggleItemVariation (itemVariation) {
    this.itemVariationId = itemVariation.id
  }

  hasModifier (modifier, modifierSetId) {
    return _.some(this.itemModifiers?.data, itemModifier => itemModifier.modifierId === modifier.id && itemModifier.modifierSetId === modifierSetId)
  }

  toggleModifier (modifier, modifierSetId) {
    if (this.hasModifier(modifier, modifierSetId)) {
      this.removeModifier(modifier, modifierSetId)
    } else {
      this.addModifier(modifier, modifierSetId)
    }
  }

  modifiersSelected (modifierSet) {
    const modifiers = _.filter(this.itemModifiers.data, itemModifier => {
      const modifier = itemModifier.modifier
      return modifier && itemModifier.modifierSetId === modifierSet.id && _.some(modifierSet.activeModifiers, { id: modifier.id })
    })
    return modifiers.length
  }

  clearAllModifiersForModifierSet (modifierSet) {
    this.itemModifiers.filter(itemModifier => {
      const modifier = itemModifier.modifier
      const matchesSetId = itemModifier.modifierSetId === modifierSet.id
      const isActive = _.some(modifierSet.activeModifiers, { id: modifier?.id })

      // Keep if: not from this set OR (from this set AND not active)
      return !matchesSetId || (modifier && !isActive)
    })
  }

  addModifier (modifier, modifierSetId) {
    if (this.hasModifier(modifier, modifierSetId)) {
      return
    }
    const itemModifierSet = _.find(this.item.itemModifierSets.data, itemModifierSet => itemModifierSet.modifierSetId === modifierSetId)
    const maxCount = itemModifierSet.finalMaxSelectionCount
    const currentCount = this.modifiersSelected(itemModifierSet.modifierSet)

    if (currentCount >= maxCount) {
      if (maxCount !== 1) {
        return
      }

      // Special behavior when only one selection allowed. Always clear other selection and add this one.
      this.clearAllModifiersForModifierSet(itemModifierSet.modifierSet)
    }

    const itemModifier = new ItemOrderItemModifier({
      modifierId: modifier.id,
      modifierSetId
    })

    this.itemModifiers.add(itemModifier)
  }

  removeModifier (modifier, modifierSetId) {
    // Can't use normal remove because we aren't removing by id, we are removing by modifier id
    this.itemModifiers.filter((model) => !(model.modifierId === modifier.id && model.modifierSetId === modifierSetId))
  }

  validateForModifierSet (itemModifierSet) {
    const selectionCount = this.modifiersSelected(itemModifierSet.modifierSet)

    if (itemModifierSet.minSelectionCount !== null && selectionCount < itemModifierSet.minSelectionCount) {
      return `Must select at least ${itemModifierSet.minSelectionCount} option${itemModifierSet.minSelectionCount === 1 ? '' : 's'}`
    }
    if (itemModifierSet.maxSelectionCount !== null && selectionCount > itemModifierSet.maxSelectionCount) {
      return `Can't select more than ${itemModifierSet.maxSelectionCount} option${itemModifierSet.maxSelectionCount === 1 ? '' : 's'}`
    }
  }

  validate () {
    if (!this.itemVariation || this.itemVariation.isLocal) return false
  }

  afterInflate () {
    if (!this.data.itemId && this.itemVariation) {
      this.data.itemId = this.itemVariation.itemId
    }
  }
}
